/**
 * Selectores fecha/hora nativos (web: date+time; iOS/Android: DateTimePicker).
 * Control chrome = shared temporal tokens (same as InventoryIsoDateField / HmTimeField).
 *
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { createElement, Fragment, useState } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { iosDatePickerDisplay } from '../../../shared/ui/iosDatePickerDisplay'
import { useModalBackdropPadding } from '../../../shared/ui/hooks-ui/useModalBackdropPadding'
import { WebTimeInput } from '../../../shared/ui/WebTimeInput'
import { webNativeTemporalInputStyle, nativeTemporalTriggerStyle, NATIVE_TEMPORAL_MIN_HEIGHT, NATIVE_TEMPORAL_RADIUS } from '../../../shared/ui/webNativeTemporalInput';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography'
import { ticketColors } from '../../../shared/theme/ticketColors';

type Props = {
  label: string;
  value: Date;
  onChange: (next: Date) => void;
  disabled?: boolean;
};

function pad(n: number) {
  return String(n).padStart(2, '0');
}

function toDateInputValue(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toTimeInputValue(d: Date) {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function mergeDatePart(base: Date, picked: Date) {
  const next = new Date(base);
  next.setFullYear(picked.getFullYear(), picked.getMonth(), picked.getDate());
  return next;
}

function mergeTimePart(base: Date, picked: Date) {
  const next = new Date(base);
  next.setHours(picked.getHours(), picked.getMinutes(), 0, 0);
  return next;
}

const DATE_OPTS: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
const TIME_OPTS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };

const webBoundDateStyle = webNativeTemporalInputStyle({ size: 'comfortable', flex: 1, minWidth: 0 });
const webBoundTimeStyle = webNativeTemporalInputStyle({
  size: 'comfortable',
  flex: 0,
  minWidth: 128,
  width: 128,
  fontWeight: '600',
});

export function LocalDateTimeField({ label, value, onChange, disabled }: Props) {
  const iosMode = iosDatePickerDisplay();
  const iosModalBackdropPadding = useModalBackdropPadding();
  const [androidMode, setAndroidMode] = useState<'date' | 'time' | null>(null);
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const [iosDraft, setIosDraft] = useState(value);

  if (Platform.OS === 'web') {
    return (
      <View style={local.field}>
        <Text style={local.fieldLabel}>{label}</Text>
        <View style={local.boundRow}>
          {createElement('input', {
            type: 'date',
            value: toDateInputValue(value),
            disabled,
            onChange: (e: { target: { value: string } }) => {
              const raw = e.target.value ?? '';
              if (!raw) return;
              const [y, m, d] = raw.split('-').map(Number);
              const next = new Date(value);
              next.setFullYear(y, m - 1, d);
              onChange(next);
            },
            style: webBoundDateStyle,
            'aria-label': `${label}, fecha`,
          })}
          {createElement('input', {
            type: 'time',
            value: toTimeInputValue(value),
            disabled,
            onChange: (e: { target: { value: string } }) => {
              const raw = e.target.value ?? '';
              if (!raw) return;
              const [h, mi] = raw.split(':').map(Number);
              const next = new Date(value);
              next.setHours(h, mi, 0, 0);
              onChange(next);
            },
            style: webBoundTimeStyle,
            'aria-label': `${label}, hora`,
          })}
        </View>
      </View>
    );
  }

  if (Platform.OS === 'android') {
    return (
      <Fragment>
        <View style={local.field}>
          <Text style={local.fieldLabel}>{label}</Text>
          <View style={local.boundRow}>
            <TouchableOpacity
              style={[local.trigger, disabled && local.disabled]}
              onPress={() => {
                if (!disabled) setAndroidMode('date');
              }}
              disabled={disabled}
              accessibilityRole="button"
              accessibilityLabel={`${label}, fecha`}
            >
              <Text style={local.triggerValue}>{value.toLocaleDateString('es-AR', DATE_OPTS)}</Text>
              <Text style={local.triggerHint}>Fecha</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[local.trigger, disabled && local.disabled]}
              onPress={() => {
                if (!disabled) setAndroidMode('time');
              }}
              disabled={disabled}
              accessibilityRole="button"
              accessibilityLabel={`${label}, hora`}
            >
              <Text style={local.triggerValue}>{value.toLocaleTimeString('es-AR', TIME_OPTS)}</Text>
              <Text style={local.triggerHint}>Hora</Text>
            </TouchableOpacity>
          </View>
        </View>
        {androidMode ? (
          <DateTimePicker
            value={value}
            mode={androidMode}
            display="default"
            is24Hour
            locale="es-AR"
            onValueChange={(_e, date) => {
              const mode = androidMode;
              setAndroidMode(null);
              onChange(mode === 'date' ? mergeDatePart(value, date) : mergeTimePart(value, date));
            }}
            onDismiss={() => setAndroidMode(null)}
          />
        ) : null}
      </Fragment>
    );
  }

  return (
    <View style={local.field}>
      <Text style={local.fieldLabel}>{label}</Text>
      {iosMode === 'inline' ? (
        <View style={local.boundRow}>
          <View style={local.iosCompactWrap}>
            <DateTimePicker
              value={value}
              mode="date"
              display="compact"
              locale="es-AR"
              disabled={disabled}
              onValueChange={(_e, date) => onChange(mergeDatePart(value, date))}
            />
          </View>
          <View style={local.iosCompactWrap}>
            <DateTimePicker
              value={value}
              mode="time"
              display="compact"
              locale="es-AR"
              disabled={disabled}
              onValueChange={(_e, date) => onChange(mergeTimePart(value, date))}
            />
          </View>
        </View>
      ) : (
        <>
          <TouchableOpacity
            style={[local.triggerFull, disabled && local.disabled]}
            onPress={() => {
              if (disabled) return;
              setIosDraft(value);
              setIosSheetOpen(true);
            }}
            disabled={disabled}
            accessibilityRole="button"
            accessibilityLabel={label}
          >
            <Text style={local.triggerValue}>
              {value.toLocaleDateString('es-AR', DATE_OPTS)} ·{' '}
              {value.toLocaleTimeString('es-AR', TIME_OPTS)}
            </Text>
          </TouchableOpacity>
          <Modal
            visible={iosSheetOpen}
            transparent
            animationType="fade"
            onRequestClose={() => setIosSheetOpen(false)}
          >
            <View style={[local.iosBackdrop, iosModalBackdropPadding]}>
              <View style={local.iosCard}>
                <Text style={local.iosTitle}>{label}</Text>
                <DateTimePicker
                  value={iosDraft}
                  mode="datetime"
                  display="spinner"
                  locale="es-AR"
                  onValueChange={(_e, date) => setIosDraft(date)}
                  style={local.iosSpinner}
                />
                <View style={local.iosActions}>
                  <TouchableOpacity style={local.outlineBtn} onPress={() => setIosSheetOpen(false)}>
                    <Text style={local.outlineBtnText}>Cancelar</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={local.primaryBtn}
                    onPress={() => {
                      onChange(iosDraft);
                      setIosSheetOpen(false);
                    }}
                  >
                    <Text style={local.primaryBtnText}>Listo</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </Modal>
        </>
      )}
    </View>
  );
}

/** HH:mm para turnos de horario. */
type TimeProps = {
  value: string;
  onChange: (hm: string) => void;
  disabled?: boolean;
  accessibilityLabel?: string;
};

function hmToDate(hm: string): Date {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm.trim());
  const d = new Date();
  d.setHours(m ? Number(m[1]) : 9, m ? Number(m[2]) : 0, 0, 0);
  return d;
}

function dateToHm(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function LocalTimeField({ value, onChange, disabled, accessibilityLabel }: TimeProps) {
  const iosMode = iosDatePickerDisplay();
  const iosModalBackdropPadding = useModalBackdropPadding();
  const [androidOpen, setAndroidOpen] = useState(false);
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const [iosDraft, setIosDraft] = useState(() => hmToDate(value));

  if (Platform.OS === 'web') {
    return (
      <WebTimeInput
        value={/^\d{2}:\d{2}$/.test(value) ? value : ''}
        onChange={onChange}
        ariaLabel={accessibilityLabel}
        disabled={disabled}
        size="compact"
        compactWidth
      />
    );
  }

  if (Platform.OS === 'android') {
    return (
      <Fragment>
        <TouchableOpacity
          style={[local.timeTrigger, disabled && local.disabled]}
          onPress={() => {
            if (!disabled) setAndroidOpen(true);
          }}
          disabled={disabled}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
        >
          <Text style={local.timeValue}>{value || '––:––'}</Text>
        </TouchableOpacity>
        {androidOpen ? (
          <DateTimePicker
            value={hmToDate(value)}
            mode="time"
            display="default"
            is24Hour
            locale="es-AR"
            onValueChange={(_e, date) => {
              setAndroidOpen(false);
              onChange(dateToHm(date));
            }}
            onDismiss={() => setAndroidOpen(false)}
          />
        ) : null}
      </Fragment>
    );
  }

  if (iosMode === 'inline') {
    return (
      <View style={[local.timeTrigger, disabled && local.disabled]}>
        <DateTimePicker
          value={hmToDate(value)}
          mode="time"
          display="compact"
          locale="es-AR"
          disabled={disabled}
          onValueChange={(_e, date) => onChange(dateToHm(date))}
        />
      </View>
    );
  }

  return (
    <>
      <TouchableOpacity
        style={[local.timeTrigger, disabled && local.disabled]}
        onPress={() => {
          if (disabled) return;
          setIosDraft(hmToDate(value));
          setIosSheetOpen(true);
        }}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
      >
        <Text style={local.timeValue}>{value || '––:––'}</Text>
      </TouchableOpacity>
      <Modal
        visible={iosSheetOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setIosSheetOpen(false)}
      >
        <View style={[local.iosBackdrop, iosModalBackdropPadding]}>
          <View style={local.iosCard}>
            {accessibilityLabel ? <Text style={local.iosTitle}>{accessibilityLabel}</Text> : null}
            <DateTimePicker
              value={iosDraft}
              mode="time"
              display="spinner"
              locale="es-AR"
              onValueChange={(_e, date) => setIosDraft(date)}
              style={local.iosSpinner}
            />
            <View style={local.iosActions}>
              <TouchableOpacity style={local.outlineBtn} onPress={() => setIosSheetOpen(false)}>
                <Text style={local.outlineBtnText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={local.primaryBtn}
                onPress={() => {
                  onChange(dateToHm(iosDraft));
                  setIosSheetOpen(false);
                }}
              >
                <Text style={local.primaryBtnText}>Listo</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const local = StyleSheet.create({
  field: { gap: spacing.xs },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  boundRow: { flexDirection: 'row', gap: spacing.sm },
  trigger: {
    ...nativeTemporalTriggerStyle,
    flex: 1,
    minWidth: 0,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
  },
  triggerFull: {
    ...nativeTemporalTriggerStyle,
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
  },
  triggerValue: {
    ...typography.body,
    fontSize: 14,
    fontWeight: '600',
    color: colors.textPrimary,
    fontVariant: ['tabular-nums'],
  },
  triggerHint: {
    ...typography.caption,
    fontSize: 11,
    color: colors.textMuted,
  },
  disabled: { opacity: 0.5 },
  iosCompactWrap: {
    flex: 1,
    minWidth: 0,
    borderWidth: 1,
    borderColor: colors.controlBorder,
    borderRadius: NATIVE_TEMPORAL_RADIUS,
    backgroundColor: colors.inputBackground,
    minHeight: NATIVE_TEMPORAL_MIN_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  iosBackdrop: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
  },
  iosCard: {
    backgroundColor: colors.surface,
    borderRadius: 14,
    padding: spacing.md,
    gap: spacing.sm,
    borderWidth: 1,
    borderColor: colors.borderSoft,
    width: '100%',
    maxWidth: 360,
    alignSelf: 'center',
  },
  iosTitle: {
    ...typography.body,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  iosSpinner: { alignSelf: 'center', width: '100%', height: 216 },
  iosActions: { flexDirection: 'row', gap: spacing.sm, justifyContent: 'flex-end' },
  outlineBtn: {
    paddingHorizontal: spacing.md,
    paddingVertical: 10,
    borderRadius: NATIVE_TEMPORAL_RADIUS,
    borderWidth: 1,
    borderColor: colors.controlBorder,
    backgroundColor: colors.controlSurface,
    minHeight: 44,
    justifyContent: 'center',
  },
  outlineBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  primaryBtn: {
    backgroundColor: colors.accent,
    borderRadius: NATIVE_TEMPORAL_RADIUS,
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    minHeight: 44,
    justifyContent: 'center',
  },
  primaryBtnText: {
    fontWeight: '700',
    color: ticketColors.goldInk,
  },
  timeTrigger: {
    width: 88,
    minHeight: NATIVE_TEMPORAL_MIN_HEIGHT,
    height: NATIVE_TEMPORAL_MIN_HEIGHT,
    borderRadius: NATIVE_TEMPORAL_RADIUS,
    borderWidth: 1,
    borderColor: colors.controlBorder,
    backgroundColor: colors.inputBackground,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.sm,
  },
  timeValue: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textPrimary,
    fontVariant: ['tabular-nums'],
  },
});
